import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';

interface ToastLocationState {
    message?: string;
    type?: 'success' | 'error' | 'info' | 'warning';
    [key: string]: unknown;
}

export function useToastNavigation() {
    const location = useLocation();
    const navigate = useNavigate();
    // Ref to track the currently shown message to prevent duplicates in React Strict Mode
    const toastShownRef = useRef<string | null>(null);

    useEffect(() => {
        const state = location.state as ToastLocationState | null;
        const message = state?.message;
        const type = state?.type || 'success';

        if (message) {
            // If the message is the same as the one we just showed, ignore it.
            // This handles the double-invocation of effects in React Strict Mode.
            if (toastShownRef.current === message) {
                return;
            }

            // Show the toast
            toaster.create({
                description: message,
                type: type,
            });

            // Mark this message as shown
            toastShownRef.current = message;

            // Clean the message from the location state
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { message: _, type: __, ...restState } = state;

            // Replace the current history entry with the cleaned state
            navigate(location.pathname, {
                replace: true,
                state: Object.keys(restState).length > 0 ? restState : undefined
            });
        } else {
            // Reset the ref when there is no message, allowing new messages to be shown
            toastShownRef.current = null;
        }
    }, [location, navigate]);
}
